import { Attachment } from "@/api/attachment";
import { LocaleDict, SupportedLocale, supportedLocales } from "@/i18n";

export type AttachmentWithLegacyFields = Partial<Attachment> & {
  legend?: string;
  legendEN?: string;
  descriptionFR?: string;
  descriptionEN?: string;
  textAltFR?: string;
  textAltEN?: string;
};

type MultilingualAttachmentField = "caption" | "textAlt" | "description";

const LEGACY_FIELD_BY_LOCALE: Record<
  MultilingualAttachmentField,
  Partial<Record<SupportedLocale, keyof AttachmentWithLegacyFields>>
> = {
  caption: { fr: "legend", en: "legendEN" },
  textAlt: { fr: "textAltFR", en: "textAltEN" },
  description: { fr: "descriptionFR", en: "descriptionEN" },
};

export function hydrateAttachmentMultilingualFields(
  attachment: AttachmentWithLegacyFields,
): Pick<Attachment, MultilingualAttachmentField> {
  const hydrated = {
    caption: { ...(attachment.caption ?? {}) },
    textAlt: { ...(attachment.textAlt ?? {}) },
    description: { ...(attachment.description ?? {}) },
  };

  for (const field of Object.keys(LEGACY_FIELD_BY_LOCALE) as MultilingualAttachmentField[]) {
    for (const [locale, legacyKey] of Object.entries(
      LEGACY_FIELD_BY_LOCALE[field],
    ) as [SupportedLocale, keyof AttachmentWithLegacyFields][]) {
      const legacyValue = attachment[legacyKey];
      if (typeof legacyValue !== "string" || legacyValue === "") continue;

      const current = hydrated[field][locale];
      if (current === undefined || current === "") {
        hydrated[field][locale] = legacyValue;
      }
    }
  }

  return hydrated;
}

type DirtyMultilingualField =
  | boolean
  | Partial<Record<SupportedLocale, boolean | undefined>>;

function mergeDirtyMultilingualField(
  existing: LocaleDict<string> | undefined,
  override: LocaleDict<string> | undefined,
  dirty: DirtyMultilingualField,
): LocaleDict<string> | undefined {
  const merged: LocaleDict<string> = { ...(existing ?? {}) };
  if (!override) return merged;

  const localesToApply =
    typeof dirty === "object"
      ? supportedLocales.filter((locale) => dirty[locale])
      : supportedLocales;

  for (const locale of localesToApply) {
    if (override[locale] !== undefined) {
      merged[locale] = override[locale];
    }
  }

  return merged;
}

export function buildAttachmentMetadataOverrides(
  existing: AttachmentWithLegacyFields,
  formData: Partial<Pick<Attachment, MultilingualAttachmentField>>,
  dirtyFields: Partial<
    Record<MultilingualAttachmentField, DirtyMultilingualField>
  >,
): Partial<Pick<Attachment, MultilingualAttachmentField>> {
  const hydrated = hydrateAttachmentMultilingualFields(existing);
  const overrides: Partial<Pick<Attachment, MultilingualAttachmentField>> = {};

  for (const field of [
    "caption",
    "textAlt",
    "description",
  ] as const satisfies readonly MultilingualAttachmentField[]) {
    const dirty = dirtyFields[field];
    if (!dirty) continue;

    overrides[field] = mergeDirtyMultilingualField(
      hydrated[field],
      formData[field],
      dirty,
    );
  }

  return overrides;
}
